import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import cs from 'classnames';

import { SidebarTabsItem } from 'types/sidebar';

import styles from './SidebarTabs.module.scss';

interface SidebarTabsProps {
  items: SidebarTabsItem[];
  isChangeCurrentTabOnNewItems?: boolean;
  children?:
    | React.ReactElement
    | React.ReactElement[]
    | string
    | null
    | undefined;
  openItemId?: number;
}

const SidebarTabs: FC<SidebarTabsProps> = ({
  items,
  isChangeCurrentTabOnNewItems = true,
  children,
  openItemId
}) => {
  const { t } = useTranslation();

  const getInitialItemId = (): number | null => {
    if (openItemId !== undefined) {
      return openItemId;
    }
    if (items.length > 0) {
      return items[0].id;
    }
    return null;
  };

  const [currentItemId, setCurrentItemId] = useState<number | null>(
    getInitialItemId()
  );
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!items.length) {
      return;
    }

    if (openItemId !== undefined) {
      setCurrentItemId(openItemId);
      isInitialized.current = true;
      return;
    }

    if (isChangeCurrentTabOnNewItems) {
      setCurrentItemId(items[0].id);
      isInitialized.current = true;
    }
  }, [items, openItemId, isChangeCurrentTabOnNewItems]);

  const currentComponent =
    items.find((el) => el.id === currentItemId)?.component || null;

  return (
    <div className={styles.container}>
      <div className={styles.tabs}>
        {items.map((item) => (
          <div
            className={cs(
              styles.item,
              currentItemId === item.id ? styles.active : ''
            )}
            key={item.id}
            onClick={() => setCurrentItemId(item.id)}
          >
            {t(item.title)}
          </div>
        ))}
      </div>
      {children && <div className={styles.children}>{children}</div>}
      <div className={styles.content}>{currentComponent}</div>
    </div>
  );
};

export default SidebarTabs;
